import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { Icon } from "@iconify/react";
import { getClinicianInviteApi, clinicianSignupApi } from "@/portals/clinician/api/clinicianAuthApi";
import { AuthTextField, AuthPasswordField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { validateFields } from "@/shared/lib/validators";
import { toastError } from "@/shared/lib/toast";
import { CONFIRM_PASSWORD_RULES, PASSWORD_MISMATCH_MESSAGE, SIGNUP_PASSWORD_RULES } from "@/shared/lib/passwordRules";
import { useClinicianAuthStore } from "@/stores/clinicianAuthStore";
import { AuthHeading } from "@/shared/components/AuthHeading";

export const INVITE_INVALID_MESSAGE = "This invitation is no longer valid. Contact your administrator.";
export const INVITE_EXPIRED_MESSAGE = "This invitation has expired. Ask your administrator to resend it.";
export const EMAIL_ALREADY_REGISTERED_MESSAGE = "An account with this email already exists. Contact your administrator.";

type Step =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "expired" }
  | { kind: "load-error" }
  | { kind: "form"; email: string };

function InviteNotice({ icon, title, message, action }: { icon: string; title: string; message: string; action?: React.ReactNode }) {
  return (
    <div className="w-full max-w-sm text-center" role="alert">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#F3F4F6]">
        <Icon icon={icon} className="h-6 w-6 text-[#9CA3AF]" />
      </div>
      <h2 className="text-xl font-extrabold text-[#0A2947]">{title}</h2>
      <p className="!mt-2 text-sm text-[#6B7280]">{message}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ClinicianInvitePage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: "loading" });

  const loadPreview = useCallback(() => {
    let cancelled = false;
    setStep({ kind: "loading" });
    getClinicianInviteApi(token).then(({ data, error }) => {
      if (cancelled) return;
      if (data) setStep({ kind: "form", email: data.email });
      else if (error?.code === "INVITE_EXPIRED") setStep({ kind: "expired" });
      else if (error?.code === "INVITE_INVALID") setStep({ kind: "invalid" });
      else setStep({ kind: "load-error" });
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => loadPreview(), [loadPreview]);

  if (step.kind === "loading") {
    return (
      <div className="flex w-full max-w-sm justify-center py-16" aria-busy="true">
        <Spin size="large" />
      </div>
    );
  }
  if (step.kind === "invalid") {
    return <InviteNotice icon="lucide:mail-x" title="Invitation unavailable" message={INVITE_INVALID_MESSAGE} />;
  }
  if (step.kind === "expired") {
    return <InviteNotice icon="lucide:clock-alert" title="Invitation expired" message={INVITE_EXPIRED_MESSAGE} />;
  }
  if (step.kind === "load-error") {
    return (
      <InviteNotice
        icon="lucide:wifi-off"
        title="Couldn't load this invitation"
        message="Check your connection and try again."
        action={<Button onClick={loadPreview} className="!rounded-[12px]">Try again</Button>}
      />
    );
  }

  return (
    <ClinicianSignupForm
      token={token}
      email={step.email}
      onSignedIn={() => navigate("/clinician", { replace: true })}
      onInviteGone={(kind) => setStep({ kind })}
    />
  );
}

interface SignupFormProps {
  token: string;
  email: string;
  onSignedIn: () => void;
  onInviteGone: (kind: "invalid" | "expired") => void;
}

/** Accepting the invitation: the email is fixed, the clinician only picks a password and lands signed in. */
function ClinicianSignupForm({ token, email, onSignedIn, onInviteGone }: SignupFormProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const clearError = (field: string) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { password, confirmPassword },
      { password: SIGNUP_PASSWORD_RULES, confirmPassword: CONFIRM_PASSWORD_RULES },
    );
    setErrors(fieldErrors as Record<string, string>);
    if (Object.keys(fieldErrors).length > 0) return;

    if (password !== confirmPassword) {
      toastError(PASSWORD_MISMATCH_MESSAGE);
      return;
    }

    setLoading(true);
    const { data, error } = await clinicianSignupApi({ token, password, confirmPassword });
    setLoading(false);

    if (data) {
      useClinicianAuthStore.getState().setSession({
        accessToken: data.accessToken,
        expiresIn: data.expiresIn ?? NaN,
        user: data.clinician ?? null,
      });
      onSignedIn();
      return;
    }
    if (error?.code === "INVITE_INVALID") onInviteGone("invalid");
    else if (error?.code === "INVITE_EXPIRED") onInviteGone("expired");
    else if (error?.code === "EMAIL_ALREADY_REGISTERED") toastError(EMAIL_ALREADY_REGISTERED_MESSAGE);
    else toastError(error?.message || "Unable to create your account.");
  };

  return (
    <div className="w-full">
      <AuthHeading title={<>Join <span className="font-extrabold tracking-tight">MedSyncIQ</span></>} subtitle="Choose a password to set up your clinician account" className="mb-9" />

      <form onSubmit={handleSubmit} noValidate>
        <AuthTextField label="Email address" icon="lucide:mail" value={email} onChange={() => {}} disabled />

        <AuthPasswordField
          required
          label="Password"
          placeholder="Password"
          autoComplete="new-password"
          icon="lucide:lock"
          value={password}
          onChange={(v) => { setPassword(v); clearError("password"); }}
          error={errors.password}
          disabled={loading}
        />
        <AuthPasswordField
          required
          label="Confirm Password"
          placeholder="Confirm Password"
          autoComplete="new-password"
          icon="lucide:lock"
          value={confirmPassword}
          onChange={(v) => { setConfirmPassword(v); clearError("confirmPassword"); }}
          error={errors.confirmPassword}
          disabled={loading}
          className="mb-7"
        />

        <AuthSubmitButton loading={loading}>Create account</AuthSubmitButton>
      </form>
    </div>
  );
}
